import React, { useState, useEffect, useMemo } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  Button,
  Table,
  Badge,
  Tabs,
  Tab,
  Form,
  Spinner,
  ButtonGroup,
  Modal,
  ListGroup,
  Alert
} from "react-bootstrap";
import { useAuth } from "../contexts/AuthContext";
import { fires, auth } from "../firebase";
import { isMobile } from "react-device-detect";
import firebase from "firebase";
import { QRCodeCanvas } from "qrcode.react";
import { DataGrid } from "@mui/x-data-grid";
import { useNavigate } from "react-router-dom";

export default function NewDashboard() {
  const { currentUser, logout } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [hasManagementRole, setHasManagementRole] = useState(false);
  const [hasDataRole, setHasDataRole] = useState(false);
  const [checkingAdmin, setCheckingAdmin] = useState(true);
  const [activeTab, setActiveTab] = useState("events");
  const [employeeInfo, setEmployeeInfo] = useState(null);
  
  // Events data
  const [events, setEvents] = useState([]);
  const [eventFilter, setEventFilter] = useState("current"); // "current" or "past"
  const [loadingEvents, setLoadingEvents] = useState(false);
  
  // Organizations data
  const [organizations, setOrganizations] = useState([]);
  const [loadingOrgs, setLoadingOrgs] = useState(false);
  
  // Payouts data
  const [payouts, setPayouts] = useState([]);
  const [loadingPayouts, setLoadingPayouts] = useState(false);
  
  // Page views data
  const [pageViews, setPageViews] = useState([]);
  const [loadingPageViews, setLoadingPageViews] = useState(false);
  
  // Live events data
  const [liveEvents, setLiveEvents] = useState([]);
  const [loadingLiveEvents, setLoadingLiveEvents] = useState(false);
  const [liveEventFilter, setLiveEventFilter] = useState("live"); // "live", "draft", "deleted", or "all"

  // Admins data
  const [admins, setAdmins] = useState([]);
  const [loadingAdmins, setLoadingAdmins] = useState(false);

  // Admin org assignment
  const [currentOrgId, setCurrentOrgId] = useState("");
  const [selectedOrgId, setSelectedOrgId] = useState("");
  const [updatingOrg, setUpdatingOrg] = useState(false);
  const [orgUpdateError, setOrgUpdateError] = useState("");
  const [orgUpdateSuccess, setOrgUpdateSuccess] = useState("");
  const [orgSearchTerm, setOrgSearchTerm] = useState("");
  const [trackingIds, setTrackingIds] = useState("");
  const [trackingTitle, setTrackingTitle] = useState("");
  const [trackingLinkInput, setTrackingLinkInput] = useState("");
  const [copiedTrackingLink, setCopiedTrackingLink] = useState("");
  const [createdTrackingLinks, setCreatedTrackingLinks] = useState([]);
  const [creatingTrackingLinks, setCreatingTrackingLinks] = useState(false);
  const [trackingCreateError, setTrackingCreateError] = useState("");
  const [trackingCreateSuccess, setTrackingCreateSuccess] = useState("");
  const [trackingLinksList, setTrackingLinksList] = useState([]);
  const [loadingTrackingLinks, setLoadingTrackingLinks] = useState(false);
  const [isTrackingLinksListOpen, setIsTrackingLinksListOpen] = useState(false);

  const maxW = isMobile ? "100%" : "90%";
  const ADMIN_API_BASE = "https://api.eventgoapi.com/api-v3";

  // Check admin status and force logout if not admin
  useEffect(() => {
    const checkAdminStatus = async () => {
      try {
        if (!currentUser) {
          await logout();
          return;
        }
        
        const adminDoc = await fires.collection('admin').doc(currentUser.uid).get();
        if (!adminDoc.exists) {
          setIsAdmin(false);
          setCheckingAdmin(false);
          await logout();
          return;
        }
        
        const adminData = adminDoc.data();
        if (!adminData.roles) {
          setIsAdmin(false);
          setCheckingAdmin(false);
          await logout();
          return;
        }
        
        // Current org from users/{uid}.orgId
        let userOrgId = "";
        const userDoc = await fires.collection("users").doc(currentUser.uid).get();
        if (userDoc.exists) {
          const userData = userDoc.data() || {};
          const rawOrgId = userData.orgId;
          if (typeof rawOrgId === "string") {
            userOrgId = rawOrgId.trim();
          } else if (rawOrgId != null) {
            userOrgId = String(rawOrgId);
          }
        }

        setEmployeeInfo({
          name: adminData.name || currentUser.displayName || 'N/A',
          email: currentUser.email || adminData.email || 'N/A',
          phone: adminData.phone || 'N/A',
          employeeId: adminData.employeeId || currentUser.uid || 'N/A',
          birthday: adminData.birthday || 'N/A',
          roles: adminData.roles || [],
          orgId: userOrgId || 'Not set'
        });
        setCurrentOrgId(userOrgId);
        setSelectedOrgId(userOrgId);
        
        setIsAdmin(true);
        setHasManagementRole(adminData.roles && adminData.roles.includes('management'));
        setHasDataRole(adminData.roles && adminData.roles.includes('data'));
        setCheckingAdmin(false);

        fires.collection('admin').doc(currentUser.uid).update({
          timestamp: firebase.firestore.Timestamp.fromDate(new Date())
        });

      } catch (err) {
        console.error('Error checking admin status:', err);
        setIsAdmin(false);
        setCheckingAdmin(false);
        await logout();
      }
    };

    checkAdminStatus();
  }, [currentUser, logout]);

  useEffect(() => {
    // Only load data if user is admin
    if (isAdmin && !checkingAdmin) {
      loadEvents();
      loadOrganizations();
      loadPayouts();
      loadPageViews();
      loadLiveEvents();
      loadAdmins();
      
      setLoading(false);
    }
  }, [isAdmin, checkingAdmin]);

  const loadEvents = async () => {
    setLoadingEvents(true);
    try {
      const now = firebase.firestore.Timestamp.fromDate(new Date());
      let query = fires.collection('events');
      
      if (eventFilter === "current") {
        query = query.where('endDate', '>', now).orderBy('endDate', 'asc');
      } else {
        query = query.where('endDate', '<', now).orderBy('endDate', 'desc');
      }
      
      const snapshot = await query.get();
      const eventsData = await Promise.all(snapshot.docs.map(async (doc) => {
        const data = doc.data();
        const eventId = doc.id;
        
        // Calculate total orders amount for this event
        let totalOrdersAmount = 0;
        try {
          const ordersSnapshot = await fires
            .collection('orders')
            .where('eventId', '==', eventId)
            .get();
          
          ordersSnapshot.docs.forEach(orderDoc => {
            const orderData = orderDoc.data();
            const amount = orderData.amount || orderData.totalAmount || 0;
            totalOrdersAmount += amount;
          });
        } catch (error) {
          console.error(`Error loading orders for event ${eventId}:`, error);
        }
        
        // Get payout status for this event
        let payoutStatus = 'No Payouts';
        let totalPayouts = 0;
        try {
          const payoutsSnapshot = await fires
            .collection('payouts')
            .where('eventId', '==', eventId)
            .get();
          
          if (payoutsSnapshot.docs.length > 0) {
            let paidAmount = 0;
            let pendingAmount = 0;
            
            payoutsSnapshot.docs.forEach(payoutDoc => {
              const payoutData = payoutDoc.data();
              const amount = payoutData.amount || 0;
              totalPayouts += amount;
              
              if (payoutData.status === 'paid') {
                paidAmount += amount;
              } else {
                pendingAmount += amount;
              }
            });
            
            if (paidAmount === totalPayouts && totalPayouts > 0) {
              payoutStatus = 'Fully Paid';
            } else if (paidAmount > 0) {
              payoutStatus = `Partial Paid (${formatCurrency(paidAmount)}/${formatCurrency(totalPayouts)})`;
            } else {
              payoutStatus = 'Pending';
            }
          }
        } catch (error) {
          console.error(`Error loading payouts for event ${eventId}:`, error);
        }
        
        return {
          id: eventId,
          name: data.name || data.title || 'Untitled Event',
          startDate: data.startDate?.toDate?.() || data.timestamp?.toDate?.() || new Date(),
          endDate: data.endDate?.toDate?.() || data.startDate?.toDate?.() || new Date(),
          location: data.location || '',
          status: data.status || 'active',
          orgId: data.orgId || '',
          createdAt: data.timestamp?.toDate?.() || new Date(),
          totalOrdersAmount: totalOrdersAmount,
          payoutStatus: payoutStatus,
          totalPayouts: totalPayouts
        };
      }));
      
      setEvents(eventsData);
    } catch (error) {
      console.error('Error loading events:', error);
    } finally {
      setLoadingEvents(false);
    }
  };

  const loadLiveEvents = async () => {
    setLoadingLiveEvents(true);
    try {
      const now = firebase.firestore.Timestamp.fromDate(new Date());
      let query = fires.collection('events');
      
      if (liveEventFilter === "live") {
        query = query.where('isLive', '==', true);
      } else if (liveEventFilter === "draft") {
        query = query.where('status', '==', 'draft');
      } else if (liveEventFilter === "deleted") {
        query = query.where('status', '==', 'deleted');
      }
      // If "all", no additional filter needed
      
      const snapshot = await query.orderBy('timestamp', 'desc').get();
        
      const liveEventsData = await Promise.all(snapshot.docs.map(async (doc) => {
        const data = doc.data();
        const eventId = doc.id;
        
        // Calculate total orders amount for this live event
        let totalOrdersAmount = 0;
        try {
          const ordersSnapshot = await fires
            .collection('orders')
            .where('eventId', '==', eventId)
            .get();
          
          ordersSnapshot.docs.forEach(orderDoc => {
            const orderData = orderDoc.data();
            const amount = orderData.amount || orderData.totalAmount || 0;
            totalOrdersAmount += amount;
          });
        } catch (error) {
          console.error(`Error loading orders for live event ${eventId}:`, error);
        }
        
        // Get payout status for this live event
        let payoutStatus = 'No Payouts';
        let totalPayouts = 0;
        try {
          const payoutsSnapshot = await fires
            .collection('payouts')
            .where('eventId', '==', eventId)
            .get();
          
          if (payoutsSnapshot.docs.length > 0) {
            let paidAmount = 0;
            let pendingAmount = 0;
            
            payoutsSnapshot.docs.forEach(payoutDoc => {
              const payoutData = payoutDoc.data();
              const amount = payoutData.amount || 0;
              totalPayouts += amount;
              
              if (payoutData.status === 'paid') {
                paidAmount += amount;
              } else {
                pendingAmount += amount;
              }
            });
            
            if (paidAmount === totalPayouts && totalPayouts > 0) {
              payoutStatus = 'Fully Paid';
            } else if (paidAmount > 0) {
              payoutStatus = `Partial Paid (${formatCurrency(paidAmount)}/${formatCurrency(totalPayouts)})`;
            } else {
              payoutStatus = 'Pending';
            }
          }
        } catch (error) {
          console.error(`Error loading payouts for live event ${eventId}:`, error);
        }
        
        return {
          id: eventId,
          name: data.name || data.title || 'Untitled Live Event',
          startDate: data.startDate?.toDate?.() || data.timestamp?.toDate?.() || new Date(),
          endDate: data.endDate?.toDate?.() || data.startDate?.toDate?.() || new Date(),
          location: data.location || '',
          status: data.status || 'live',
          orgId: data.orgId || '',
          createdAt: data.timestamp?.toDate?.() || new Date(),
          totalOrdersAmount: totalOrdersAmount,
          payoutStatus: payoutStatus,
          totalPayouts: totalPayouts,
          isLive: true
        };
      }));
      
      setLiveEvents(liveEventsData);
    } catch (error) {
      console.error('Error loading live events:', error);
    } finally {
      setLoadingLiveEvents(false);
    }
  };

  const loadOrganizations = async () => {
    setLoadingOrgs(true);
    try {
      const idToken = await auth.currentUser.getIdToken(true);
      const response = await fetch(`${ADMIN_API_BASE}/admin/organizations?limit=500`, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${idToken}`
        }
      });

      if (!response.ok) {
        let message = `Request failed (${response.status})`;
        try {
          const errData = await response.json();
          message = errData.message || errData.error || message;
        } catch (_) {
          // ignore JSON parse errors
        }
        throw new Error(message);
      }

      const data = await response.json();
      const apiOrganizations = Array.isArray(data.organizations) ? data.organizations : [];
      const orgsData = apiOrganizations.map((organization) => {
        const timestamp = organization.timestamp
          ? new Date(organization.timestamp)
          : null;

        return {
          ...organization,
          id: organization.id || organization.orgId,
          orgId: organization.orgId || organization.id,
          name: organization.name || "Untitled Organization",
          email: organization.email || "",
          plan: organization.plan || "free",
          memberCount: organization.memberCount || 0,
          eventCount: organization.eventCount || organization.event_count || 0,
          createdAt: timestamp && !Number.isNaN(timestamp.getTime()) ? timestamp : null,
          status: organization.status || "active"
        };
      });
      
      setOrganizations(orgsData);
    } catch (error) {
      console.error('Error loading organizations:', error);
    } finally {
      setLoadingOrgs(false);
    }
  };

  const loadPayouts = async () => {
    setLoadingPayouts(true);
    try {
      // Load payouts from all organizations
      const orgsSnapshot = await fires.collection('groups').get();
      const allPayouts = [];
      
      for (const orgDoc of orgsSnapshot.docs) {
        const payoutsSnapshot = await fires
          .collection('payouts')
          .orderBy('timestamp', 'desc')
          .get();
          
        payoutsSnapshot.docs.forEach(payoutDoc => {
          const payoutData = payoutDoc.data();
          allPayouts.push({
            id: payoutDoc.id,
            orgId: orgDoc.id,
            orgName: orgDoc.data().name || orgDoc.data().groupName || 'Unknown Organization',
            amount: payoutData.amount || 0,
            currency: payoutData.currency || 'CAD',
            status: payoutData.status || 'pending',
            eventId: payoutData.eventId || '',
            eventName: payoutData.eventName || '',
            timestamp: payoutData.timestamp?.toDate?.() || new Date(),
            description: payoutData.description || ''
          });
        });
      }
      
      // Sort by timestamp
      allPayouts.sort((a, b) => b.timestamp - a.timestamp);
      setPayouts(allPayouts);
    } catch (error) {
      console.error('Error loading payouts:', error);
    } finally {
      setLoadingPayouts(false);
    }
  };

  const loadPageViews = async () => {
    setLoadingPageViews(true);
    try {
      const snapshot = await fires
        .collection('pageViews')
        .orderBy('timestamp', 'desc')
        .get();
        
      const pageViewsData = snapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          page: data.page || '',
          userAgent: data.userAgent || '',
          ip: data.ip || '',
          timestamp: data.timestamp?.toDate?.() || new Date(),
          userId: data.userId || '',
          sessionId: data.sessionId || ''
        };
      });
      
      setPageViews(pageViewsData);
    } catch (error) {
      console.error('Error loading page views:', error);
    } finally {
      setLoadingPageViews(false);
    }
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatCurrency = (amount, currency = 'USD') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency
    }).format(amount);
  };

  const normalizeTrackingLinkDoc = (doc) => {
    const data = doc.data ? doc.data() : doc;
    const timestamp =
      data.timestamp?.toDate?.() ||
      data.createdAt?.toDate?.() ||
      data.updatedAt?.toDate?.() ||
      null;

    return {
      id: doc.id || data.id || "",
      title: data.trackingTitle || data.title || data.name || "",
      trackingId: data.trackingId || data.channel || "",
      channel: data.channel || data.trackingId || "",
      url: data.url || "",
      originalUrl: data.originalUrl || "",
      orgId: data.orgId || "",
      createdByEmail: data.createdByEmail || "",
      timestamp
    };
  };

  const loadTrackingLinks = async () => {
    setLoadingTrackingLinks(true);
    try {
      const snapshot = await fires
        .collection("links")
        .where("type", "==", "tracking")
        .get();

      const linksData = snapshot.docs
        .map(normalizeTrackingLinkDoc)
        .sort((a, b) => {
          const aTime = a.timestamp ? a.timestamp.getTime() : 0;
          const bTime = b.timestamp ? b.timestamp.getTime() : 0;
          return bTime - aTime;
        });

      setTrackingLinksList(linksData);
    } catch (error) {
      console.error("Error loading tracking links:", error);
    } finally {
      setLoadingTrackingLinks(false);
    }
  };

  const handleToggleTrackingLinksList = async () => {
    const shouldOpen = !isTrackingLinksListOpen;
    setIsTrackingLinksListOpen(shouldOpen);

    if (shouldOpen && trackingLinksList.length === 0) {
      await loadTrackingLinks();
    }
  };

  const loadAdmins = async () => {
    setLoadingAdmins(true);
    try {
      const snapshot = await fires.collection('admin').get();
      const adminsData = await Promise.all(snapshot.docs.map(async (doc) => {
        const data = doc.data();
        const uid = doc.id;
        
        // Try to get user email from auth or users collection
        let email = data.email || '';
        
        
        return {
          id: uid,
          name: data.name || 'N/A',
          email: email || 'N/A',
          birthday: data.birthday || 'N/A',
          roles: data.roles || [],
          createdAt: data.createdAt?.toDate?.() || null,
          lastLogin: data.timestamp?.toDate?.() || null,
          status: data.status || 'active'
        };
      }));
      
      // Sort by name or email
      adminsData.sort((a, b) => {
        const nameA = a.name.toLowerCase();
        const nameB = b.name.toLowerCase();
        if (nameA < nameB) return -1;
        if (nameA > nameB) return 1;
        return 0;
      });
      
      setAdmins(adminsData);
    } catch (error) {
      console.error('Error loading admins:', error);
    } finally {
      setLoadingAdmins(false);
    }
  };

  const handleLogout = async () => {
    try {
      await logout();
      window.location.href = "/";
    } catch (error) {
      console.error('Error logging out:', error);
    }
  };

  const filteredOrganizations = useMemo(() => {
    const term = orgSearchTerm.trim().toLowerCase();
    let list = organizations;

    if (term) {
      list = organizations.filter((org) => {
        const name = (org.name || "").toLowerCase();
        const id = (org.id || "").toLowerCase();
        const email = (org.email || "").toLowerCase();
        return name.includes(term) || id.includes(term) || email.includes(term);
      });
    }

    if (selectedOrgId && !list.some((org) => org.id === selectedOrgId)) {
      const selected = organizations.find((org) => org.id === selectedOrgId);
      if (selected) {
        list = [selected, ...list];
      }
    }

    return list;
  }, [organizations, orgSearchTerm, selectedOrgId]);

  const parsedTrackingIds = useMemo(() => {
    return trackingIds
      .split(/[\n,]+/)
      .map((id) => id.trim())
      .filter(Boolean);
  }, [trackingIds]);

  const invalidTrackingIds = useMemo(() => {
    return parsedTrackingIds.filter((id) => !/^[A-Za-z0-9]+$/.test(id));
  }, [parsedTrackingIds]);

  const trackingLinks = useMemo(() => {
    const rawLink = trackingLinkInput.trim();
    const title = trackingTitle.trim();

    if (!rawLink || !title || parsedTrackingIds.length === 0 || invalidTrackingIds.length > 0) {
      return [];
    }

    const origin = typeof window !== "undefined" && window.location?.origin
      ? window.location.origin
      : "https://letjoy.app";

    try {
      const baseUrl = new URL(rawLink, origin);

      return parsedTrackingIds.map((id) => {
        const url = new URL(baseUrl.toString());
        url.searchParams.set("channel", id);

        return {
          id,
          title,
          url: url.toString()
        };
      });
    } catch (err) {
      return [];
    }
  }, [trackingLinkInput, trackingTitle, parsedTrackingIds, invalidTrackingIds]);

  const handleCopyTrackingLink = async (url) => {
    try {
      await navigator.clipboard.writeText(url);
      setCopiedTrackingLink(url);
    } catch (err) {
      console.error("Error copying tracking link:", err);
    }
  };

  const resetCreatedTrackingLinks = () => {
    setCreatedTrackingLinks([]);
    setTrackingCreateError("");
    setTrackingCreateSuccess("");
    setCopiedTrackingLink("");
  };

  const handleCreateTrackingLinks = async () => {
    if (!currentUser) return;

    if (!trackingLinkInput.trim()) {
      setTrackingCreateError("Please enter a link.");
      setTrackingCreateSuccess("");
      return;
    }

    if (!trackingTitle.trim()) {
      setTrackingCreateError("Please enter a tracking title.");
      setTrackingCreateSuccess("");
      return;
    }

    if (parsedTrackingIds.length === 0) {
      setTrackingCreateError("Please add or generate at least one tracking ID.");
      setTrackingCreateSuccess("");
      return;
    }

    if (invalidTrackingIds.length > 0) {
      setTrackingCreateError("Tracking IDs can only contain English letters and numbers.");
      setTrackingCreateSuccess("");
      return;
    }

    if (trackingLinks.length === 0) {
      setTrackingCreateError("Unable to create tracking links. Please check the link field.");
      setTrackingCreateSuccess("");
      return;
    }

    try {
      setCreatingTrackingLinks(true);
      setTrackingCreateError("");
      setTrackingCreateSuccess("");

      const orgId = selectedOrgId || currentOrgId || "";
      const createdLinks = await Promise.all(
        trackingLinks.map(async (link) => {
          const createdAt = new Date();
          const docData = {
            type: "tracking",
            title: link.title,
            name: link.title,
            url: link.url,
            originalUrl: trackingLinkInput.trim(),
            trackingId: link.id,
            trackingTitle: link.title,
            channel: link.id,
            orgId,
            createdBy: currentUser.uid,
            createdByEmail: currentUser.email || "",
            source: "dashboard",
            utm: {
              source: "admin",
              medium: "tracking_link",
              campaign: link.title,
              content: link.id
            },
            timestamp: firebase.firestore.FieldValue.serverTimestamp()
          };
          const docRef = await fires.collection("links").add(docData);
          return {
            ...link,
            docId: docRef.id,
            trackingId: link.id,
            channel: link.id,
            originalUrl: trackingLinkInput.trim(),
            orgId,
            createdByEmail: currentUser.email || "",
            timestamp: createdAt
          };
        })
      );

      setCreatedTrackingLinks(createdLinks);
      setTrackingLinksList((prev) => [
        ...createdLinks.map((link) => ({
          id: link.docId,
          title: link.title,
          trackingId: link.trackingId,
          channel: link.channel,
          url: link.url,
          originalUrl: link.originalUrl,
          orgId: link.orgId,
          createdByEmail: link.createdByEmail,
          timestamp: link.timestamp
        })),
        ...prev.filter((link) => !createdLinks.some((created) => created.docId === link.id))
      ]);
      setTrackingCreateSuccess(`Created ${createdLinks.length} tracking link${createdLinks.length === 1 ? "" : "s"}.`);
    } catch (err) {
      console.error("Error creating tracking links:", err);
      setTrackingCreateError(err.message || "Failed to create tracking links.");
    } finally {
      setCreatingTrackingLinks(false);
    }
  };

  const generateTrackingId = () => {
    const chars = "abcdefghijklmnopqrstuvwxyz0123456789";
    const makeId = () => {
      let id = "";
      const values = new Uint32Array(8);

      if (window.crypto?.getRandomValues) {
        window.crypto.getRandomValues(values);
      } else {
        for (let i = 0; i < values.length; i += 1) {
          values[i] = Math.floor(Math.random() * chars.length);
        }
      }

      for (let i = 0; i < values.length; i += 1) {
        id += chars[values[i] % chars.length];
      }

      return id;
    };

    let nextId = makeId();
    while (parsedTrackingIds.includes(nextId)) {
      nextId = makeId();
    }

    setTrackingIds((prev) => {
      const current = prev.trim();
      return current ? `${current}\n${nextId}` : nextId;
    });
    resetCreatedTrackingLinks();
  };

  const handleDownloadTrackingQr = (qrId, trackingId) => {
    const canvas = document.getElementById(qrId);
    if (!canvas) return;

    const image = canvas.toDataURL("image/png");
    const anchor = document.createElement("a");
    anchor.href = image;
    anchor.download = `tracking-link-${trackingId}.png`;
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
  };

  const handleUpdateOrg = async () => {
    if (!currentUser) return;
    if (!selectedOrgId) {
      setOrgUpdateError("Please select an organization.");
      setOrgUpdateSuccess("");
      return;
    }
    if (selectedOrgId === currentOrgId) {
      setOrgUpdateError("You are already assigned to this organization.");
      setOrgUpdateSuccess("");
      return;
    }

    try {
      setUpdatingOrg(true);
      setOrgUpdateError("");
      setOrgUpdateSuccess("");

      const idToken = await auth.currentUser.getIdToken(true);
      const response = await fetch(
        `${ADMIN_API_BASE}/admin/users/${currentUser.uid}/org`,
        {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${idToken}`
          },
          body: JSON.stringify({ orgId: selectedOrgId })
        }
      );

      if (!response.ok) {
        let message = `Request failed (${response.status})`;
        try {
          const errData = await response.json();
          message = errData.message || errData.error || message;
        } catch (_) {
          // ignore JSON parse errors
        }
        throw new Error(message);
      }

      setCurrentOrgId(selectedOrgId);
      setEmployeeInfo((prev) => (prev ? { ...prev, orgId: selectedOrgId } : prev));
      setOrgUpdateSuccess("Organization updated successfully.");
    } catch (err) {
      console.error("Error updating admin org:", err);
      setOrgUpdateError(err.message || "Failed to update organization.");
    } finally {
      setUpdatingOrg(false);
    }
  };

  const getStatusBadge = (status) => {
    const variants = {
      'active': 'success',
      'pending': 'warning',
      'completed': 'success',
      'cancelled': 'danger',
      'free': 'secondary',
      'premium': 'primary',
      'enterprise': 'dark'
    };
    return <Badge bg={variants[status] || 'secondary'}>{status}</Badge>;
  };

  // Show loading while checking admin status or loading data
  if (checkingAdmin || (loading && !isAdmin)) {
    return (
      <Container className="d-flex justify-content-center align-items-center" style={{ minHeight: '100vh' }}>
        <div className="text-center">
          <Spinner animation="border" role="status">
            <span className="visually-hidden">Checking access...</span>
          </Spinner>
          <p className="mt-3">{checkingAdmin ? 'Verifying admin access...' : 'Loading...'}</p>
        </div>
      </Container>
    );
  }

  // Don't render dashboard if not admin (should have logged out by now, but just in case)
  if (!isAdmin) {
    return (
      <Container className="d-flex justify-content-center align-items-center" style={{ minHeight: '100vh' }}>
        <div className="text-center">
          <p>Access denied. Please contact an administrator.</p>
        </div>
      </Container>
    );
  }


  return (
    <Container style={{ minHeight: '100vh', backgroundColor: 'white' }} className="py-4">
      <div style={{ width: maxW, margin: '0 auto' }}>
        <Card className="border-0" style={{ backgroundColor: 'transparent' }}>
          <Card.Body>
            <div className="mb-4">
              <div className={`d-flex ${isMobile ? 'flex-column' : 'justify-content-between'} align-items-${isMobile ? 'start' : 'center'} mb-3`}>
                <div className={isMobile ? 'mb-3' : ''}>
                  <Card.Title style={{ fontSize: '25px', fontWeight: 'bold', margin: 0 }}>
                    Dashboard
                  </Card.Title>
                  <p className="text-muted mb-0">Comprehensive overview of events, organizations, and analytics</p>
                </div>
                <div className={`d-flex align-items-center gap-3 ${isMobile ? 'w-100 justify-content-between' : ''}`}>
                  <div className={`text-${isMobile ? 'start' : 'end'}`}>
                    <div className="fw-bold">{currentUser?.email || 'User'}</div>
                    <small className="text-muted">Logged in</small>
                  </div>
                  <div className="d-flex gap-2">
                    <Button 
                      variant="outline-secondary" 
                      size="sm"
                      onClick={() => window.location.href = '/legacy-dashboard'}
                    >
                      <i className="fas fa-history me-1"></i>
                      Legacy Dashboard
                    </Button>
                    <Button 
                      variant="outline-danger" 
                      size="sm"
                      onClick={handleLogout}
                    >
                      <i className="fas fa-sign-out-alt me-1"></i>
                      Logout
                    </Button>
                  </div>
                </div>
              </div>
            </div>

            {/* Employee Info Card */}
            {employeeInfo && (
              <Card className="mb-4">
                <Card.Header>
                  <h6 className="mb-0">Employee Information</h6>
                </Card.Header>
                <Card.Body>
                  <Row>
                    <Col md={6} lg={4} className="mb-3">
                      <div>
                        <small className="text-muted d-block">Name</small>
                        <div className="fw-bold">{employeeInfo.name}</div>
                      </div>
                    </Col>
                    <Col md={6} lg={4} className="mb-3">
                      <div>
                        <small className="text-muted d-block">Email</small>
                        <div className="fw-bold">{employeeInfo.email}</div>
                      </div>
                    </Col>
                    <Col md={6} lg={4} className="mb-3">
                      <div>
                        <small className="text-muted d-block">Phone</small>
                        <div className="fw-bold">{employeeInfo.phone}</div>
                      </div>
                    </Col>
                    <Col md={6} lg={4} className="mb-3">
                      <div>
                        <small className="text-muted d-block">Employee ID</small>
                        <div className="fw-bold">{employeeInfo.employeeId}</div>
                      </div>
                    </Col>
                    <Col md={6} lg={4} className="mb-3">
                      <div>
                        <small className="text-muted d-block">Birthday</small>
                        <div className="fw-bold">{employeeInfo.birthday}</div>
                      </div>
                    </Col>
                    <Col md={6} lg={4} className="mb-3">
                      <div>
                        <small className="text-muted d-block">Current Org ID</small>
                        <div className="fw-bold">
                          <code>{employeeInfo.orgId || "Not set"}</code>
                        </div>
                      </div>
                    </Col>
                    <Col md={6} lg={8} className="mb-3">
                      <div>
                        <small className="text-muted d-block">Roles</small>
                        <div className="d-flex flex-wrap gap-2 mt-1">
                          {employeeInfo.roles && employeeInfo.roles.length > 0 ? (
                            employeeInfo.roles.map((role, idx) => (
                              <Badge key={idx} bg="primary" className="fs-6">
                                {role}
                              </Badge>
                            ))
                          ) : (
                            <span className="text-muted">No roles assigned</span>
                          )}
                        </div>
                      </div>
                    </Col>
                  </Row>
                </Card.Body>
              </Card>
            )}

            <Card className="mb-4">
              <Card.Header>
                <h6 className="mb-0">Change Organization</h6>
              </Card.Header>
              <Card.Body>
                <p className="text-muted small mb-3">
                  Select an organization, then update your assigned org for this admin account.
                </p>
                {orgUpdateSuccess && (
                  <Alert variant="success" dismissible onClose={() => setOrgUpdateSuccess("")}>
                    {orgUpdateSuccess}
                  </Alert>
                )}
                {orgUpdateError && (
                  <Alert variant="danger" dismissible onClose={() => setOrgUpdateError("")}>
                    {orgUpdateError}
                  </Alert>
                )}
                <Row className="g-3 align-items-end">
                  <Col md={8}>
                    <Form.Group controlId="adminOrgSelect">
                      <Form.Label>Organization</Form.Label>
                      {loadingOrgs ? (
                        <div className="d-flex align-items-center gap-2 text-muted">
                          <Spinner animation="border" size="sm" />
                          Loading organizations...
                        </div>
                      ) : (
                        <>
                          <Form.Control
                            type="search"
                            placeholder="Search by name, org ID, or email..."
                            value={orgSearchTerm}
                            onChange={(e) => setOrgSearchTerm(e.target.value)}
                            className="mb-2"
                          />
                          <Form.Select
                            value={selectedOrgId}
                            onChange={(e) => {
                              setSelectedOrgId(e.target.value);
                              setOrgUpdateError("");
                              setOrgUpdateSuccess("");
                            }}
                          >
                            <option value="">Select organization...</option>
                            {filteredOrganizations.map((org) => (
                              <option key={org.id} value={org.id}>
                                {org.name} ({org.id}) — {org.eventCount} event{org.eventCount === 1 ? "" : "s"}
                              </option>
                            ))}
                          </Form.Select>
                          <Form.Text className="text-muted">
                            {orgSearchTerm.trim()
                              ? `Showing ${filteredOrganizations.length} of ${organizations.length} organizations`
                              : `${organizations.length} organizations`}
                            {orgSearchTerm.trim() && filteredOrganizations.length === 0 && (
                              <span className="text-danger ms-1">— no matches</span>
                            )}
                          </Form.Text>
                        </>
                      )}
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Button
                      variant="primary"
                      className="w-100"
                      onClick={handleUpdateOrg}
                      disabled={updatingOrg || loadingOrgs || !selectedOrgId}
                    >
                      {updatingOrg ? (
                        <>
                          <Spinner animation="border" size="sm" className="me-2" />
                          Updating...
                        </>
                      ) : (
                        "Update Organization"
                      )}
                    </Button>
                  </Col>
                </Row>

                <hr className="my-4" />

                <div className="d-flex justify-content-between align-items-start gap-3 flex-wrap mb-3">
                  <div>
                    <h6 className="mb-1">Tracking Links</h6>
                    <p className="text-muted small mb-0">
                      Admin-only tool for generating tracking URLs with UTM parameters and QR codes.
                    </p>
                  </div>
                  <Badge bg="light" text="dark" className="border">Admin created</Badge>
                </div>

                <Row className="g-3">
                  <Col md={12}>
                    <Form.Group controlId="trackingDestinationLink">
                      <Form.Label>Link</Form.Label>
                      <Form.Control
                        type="url"
                        placeholder="https://example.com/page"
                        value={trackingLinkInput}
                        onChange={(e) => {
                          setTrackingLinkInput(e.target.value);
                          resetCreatedTrackingLinks();
                        }}
                      />
                      <Form.Text className="text-muted">
                        Generated links append UTM parameters, tracking fields, and channel.
                      </Form.Text>
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group controlId="trackingIds">
                      <div className="d-flex justify-content-between align-items-center gap-2 mb-2">
                        <Form.Label className="mb-0">Tracking IDs</Form.Label>
                        <Button
                          variant="outline-primary"
                          size="sm"
                          type="button"
                          onClick={generateTrackingId}
                        >
                          <i className="fas fa-plus me-1"></i>
                          Generate ID
                        </Button>
                      </div>
                      <Form.Control
                        as="textarea"
                        rows={4}
                        placeholder="aaa111, bbb222"
                        value={trackingIds}
                        isInvalid={invalidTrackingIds.length > 0}
                        onChange={(e) => {
                          setTrackingIds(e.target.value);
                          resetCreatedTrackingLinks();
                        }}
                      />
                      <Form.Control.Feedback type="invalid">
                        Tracking IDs can only contain English letters and numbers: {invalidTrackingIds.join(", ")}
                      </Form.Control.Feedback>
                      <Form.Text className="text-muted">
                        Separate multiple IDs with commas or new lines.
                      </Form.Text>
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group controlId="trackingTitle">
                      <Form.Label>Tracking Title</Form.Label>
                      <Form.Control
                        type="text"
                        placeholder="Example: Summer campaign"
                        value={trackingTitle}
                        onChange={(e) => {
                          setTrackingTitle(e.target.value);
                          resetCreatedTrackingLinks();
                        }}
                      />
                      <Form.Text className="text-muted">
                        Used as the UTM campaign and tracking title.
                      </Form.Text>
                    </Form.Group>
                  </Col>
                </Row>

                <div className="d-flex justify-content-end mt-3">
                  <Button
                    variant="primary"
                    type="button"
                    onClick={handleCreateTrackingLinks}
                    disabled={
                      creatingTrackingLinks ||
                      !trackingLinkInput.trim() ||
                      !trackingTitle.trim() ||
                      parsedTrackingIds.length === 0 ||
                      invalidTrackingIds.length > 0
                    }
                  >
                    {creatingTrackingLinks ? (
                      <>
                        <Spinner animation="border" size="sm" className="me-2" />
                        Creating...
                      </>
                    ) : (
                      <>
                        <i className="fas fa-link me-1"></i>
                        Create Link
                      </>
                    )}
                  </Button>
                </div>

                <div className="mt-3">
                  {trackingCreateSuccess && (
                    <Alert variant="success" dismissible onClose={() => setTrackingCreateSuccess("")}>
                      {trackingCreateSuccess}
                    </Alert>
                  )}
                  {trackingCreateError && (
                    <Alert variant="danger" dismissible onClose={() => setTrackingCreateError("")}>
                      {trackingCreateError}
                    </Alert>
                  )}
                  {createdTrackingLinks.length > 0 ? (
                    <ListGroup>
                      {createdTrackingLinks.map((link, index) => {
                        const qrId = `tracking-link-qr-${index}`;
                        return (
                        <ListGroup.Item
                          key={link.docId || `${link.id}-${link.url}`}
                          className="d-flex flex-column flex-lg-row justify-content-between align-items-lg-center gap-3"
                        >
                          <div className="min-w-0">
                            <div className="fw-bold">{link.title}</div>
                            <div className="small text-muted">Tracking ID: {link.id}</div>
                            {link.docId && (
                              <div className="small text-muted">Firestore ID: {link.docId}</div>
                            )}
                            <a
                              href={link.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="small text-break"
                            >
                              {link.url}
                            </a>
                          </div>
                          <div className="d-flex align-items-center gap-2 flex-wrap">
                            <QRCodeCanvas
                              id={qrId}
                              value={link.url}
                              size={96}
                              bgColor="#ffffff"
                              fgColor="#000000"
                              level="M"
                              includeMargin
                            />
                            <div className="d-flex flex-column gap-2">
                              <Button
                                variant={copiedTrackingLink === link.url ? "success" : "outline-secondary"}
                                size="sm"
                                onClick={() => handleCopyTrackingLink(link.url)}
                              >
                                {copiedTrackingLink === link.url ? (
                                  <>
                                    <i className="fas fa-check me-1"></i>
                                    Copied
                                  </>
                                ) : (
                                  <>
                                    <i className="fas fa-copy me-1"></i>
                                    Copy
                                  </>
                                )}
                              </Button>
                              <Button
                                variant="outline-primary"
                                size="sm"
                                onClick={() => handleDownloadTrackingQr(qrId, link.id)}
                              >
                                <i className="fas fa-download me-1"></i>
                                QR
                              </Button>
                            </div>
                          </div>
                        </ListGroup.Item>
                        );
                      })}
                    </ListGroup>
                  ) : (
                    <div className="text-muted small border rounded p-3">
                      Enter a link, alphanumeric tracking IDs, and a tracking title, then press Create Link.
                    </div>
                  )}

                  <hr className="my-4" />

                  <div className="d-flex justify-content-between align-items-center gap-3 flex-wrap mb-3">
                    <div>
                      <h6 className="mb-1">Tracking Links List</h6>
                      <p className="text-muted small mb-0">
                        Saved tracking links from the Firestore links collection.
                      </p>
                    </div>
                    <Button
                      variant={isTrackingLinksListOpen ? "secondary" : "outline-primary"}
                      size="sm"
                      type="button"
                      onClick={handleToggleTrackingLinksList}
                      disabled={loadingTrackingLinks}
                    >
                      {loadingTrackingLinks ? (
                        <>
                          <Spinner animation="border" size="sm" className="me-2" />
                          Loading...
                        </>
                      ) : isTrackingLinksListOpen ? (
                        <>
                          <i className="fas fa-eye-slash me-1"></i>
                          Close
                        </>
                      ) : (
                        <>
                          <i className="fas fa-eye me-1"></i>
                          Open
                        </>
                      )}
                    </Button>
                  </div>

                  {!isTrackingLinksListOpen ? (
                    <div className="text-muted small border rounded p-3">
                      Tracking links are hidden. Press Open to view the full saved list.
                    </div>
                  ) : loadingTrackingLinks ? (
                    <div className="d-flex align-items-center gap-2 text-muted border rounded p-3">
                      <Spinner animation="border" size="sm" />
                      Loading tracking links...
                    </div>
                  ) : trackingLinksList.length > 0 ? (
                    <div className="table-responsive">
                      <Table bordered hover size="sm" className="align-middle mb-0">
                        <thead>
                          <tr>
                            <th>Title</th>
                            <th>Tracking ID</th>
                            <th>Link</th>
                            <th>Org</th>
                            <th>Created</th>
                            <th>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {trackingLinksList.map((link, index) => {
                            const savedQrId = `saved-tracking-link-qr-${index}`;
                            const trackingLabel = link.trackingId || link.channel || link.id || "qr";

                            return (
                            <tr key={link.id || `${link.trackingId}-${link.url}`}>
                              <td>
                                <div className="fw-bold">{link.title || "Untitled"}</div>
                                {link.createdByEmail && (
                                  <div className="small text-muted">{link.createdByEmail}</div>
                                )}
                              </td>
                              <td>
                                <code>{link.trackingId || link.channel || "N/A"}</code>
                              </td>
                              <td style={{ minWidth: "280px" }}>
                                {link.url ? (
                                  <div className="d-flex align-items-center gap-3 flex-wrap">
                                    <QRCodeCanvas
                                      id={savedQrId}
                                      value={link.url}
                                      size={76}
                                      bgColor="#ffffff"
                                      fgColor="#000000"
                                      level="M"
                                      includeMargin
                                    />
                                    <a
                                      href={link.url}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="small text-break flex-grow-1"
                                    >
                                      {link.url}
                                    </a>
                                  </div>
                                ) : (
                                  <span className="text-muted">No URL</span>
                                )}
                              </td>
                              <td>{link.orgId || "N/A"}</td>
                              <td>{link.timestamp ? formatDate(link.timestamp) : "N/A"}</td>
                              <td>
                                <Button
                                  variant={copiedTrackingLink === link.url ? "success" : "outline-secondary"}
                                  size="sm"
                                  type="button"
                                  onClick={() => handleCopyTrackingLink(link.url)}
                                  disabled={!link.url}
                                >
                                  {copiedTrackingLink === link.url ? "Copied" : "Copy"}
                                </Button>
                                <Button
                                  variant="outline-primary"
                                  size="sm"
                                  type="button"
                                  className="ms-2"
                                  onClick={() => handleDownloadTrackingQr(savedQrId, trackingLabel)}
                                  disabled={!link.url}
                                >
                                  QR
                                </Button>
                              </td>
                            </tr>
                            );
                          })}
                        </tbody>
                      </Table>
                    </div>
                  ) : (
                    <div className="text-muted small border rounded p-3">
                      No tracking links found.
                    </div>
                  )}
                </div>
              </Card.Body>
            </Card>

            <Tabs
              id="dashboard-tabs"
              activeKey={activeTab}
              onSelect={(k) => setActiveTab(k)}
              className="mb-4"
            >
              {/* Events Tab */}
              <Tab eventKey="events" title="Events">
                <Card>
                  <Card.Header className="d-flex justify-content-between align-items-center">
                    <h6 className="mb-0">Events Management</h6>
                    <div className="d-flex gap-2">
                      <ButtonGroup>
                        <Button
                          variant={eventFilter === "current" ? "primary" : "outline-primary"}
                          size="sm"
                          onClick={() => {
                            setEventFilter("current");
                            loadEvents();
                          }}
                        >
                          Current Events
                        </Button>
                        <Button
                          variant={eventFilter === "past" ? "primary" : "outline-primary"}
                          size="sm"
                          onClick={() => {
                            setEventFilter("past");
                            loadEvents();
                          }}
                        >
                          Past Events
                        </Button>
                      </ButtonGroup>
                      <Button variant="outline-secondary" size="sm" onClick={loadEvents}>
                        {loadingEvents ? <Spinner size="sm" /> : 'Refresh'}
                      </Button>
                    </div>
                  </Card.Header>
                  <Card.Body>
                    {loadingEvents ? (
                      <div className="text-center py-4">
                        <Spinner animation="border" />
                        <p className="mt-2">Loading events...</p>
                      </div>
                    ) : events.length === 0 ? (
                      <div className="text-center py-4 text-muted">
                        No {eventFilter} events found.
                      </div>
                    ) : (
                      <div 
                        style={{ 
                          height: 400, 
                          width: '100%',
                          overflowX: 'auto',
                          overflowY: 'auto'
                        }}
                        className="table-responsive"
                      >
                        <div style={{ minWidth: isMobile ? 800 : '100%' }}>
                          <DataGrid
                            rows={events}
                            columns={[
                              { field: 'name', headerName: 'Event Name', flex: 1.5, minWidth: 150 },
                              { field: 'startDate', headerName: 'Start Date', flex: 1, minWidth: 120,
                                renderCell: (params) => formatDate(params.value) },
                              { field: 'endDate', headerName: 'End Date', flex: 1, minWidth: 120,
                                renderCell: (params) => formatDate(params.value) },
                              { field: 'location', headerName: 'Location', flex: 1, minWidth: 120 },
                              { field: 'totalOrdersAmount', headerName: 'Orders Total', flex: 1, minWidth: 120,
                                renderCell: (params) => formatCurrency(params.value) },
                              { field: 'payoutStatus', headerName: 'Payout Status', flex: 1.2, minWidth: 130,
                                renderCell: (params) => {
                                  const status = params.value;
                                  let badgeVariant = 'secondary';
                                  if (status === 'paid') badgeVariant = 'success';
                                  else if (status === 'pending') badgeVariant = 'warning';
                                  else if (status === 'pending') badgeVariant = 'danger';
                                  else if (status === 'No Payouts') badgeVariant = 'secondary';
                                  
                                  return <Badge bg={badgeVariant}>{status}</Badge>;
                                }},
                              { field: 'status', headerName: 'Event Status', flex: 0.8, minWidth: 100,
                                renderCell: (params) => getStatusBadge(params.value) },
                              { field: 'orgId', headerName: 'Org ID', flex: 0.8, minWidth: 100 }
                            ]}
                            pageSize={10}
                            rowsPerPageOptions={[10, 25, 50]}
                            disableSelectionOnClick
                            autoHeight={false}
                          />
                        </div>
                      </div>
                    )}
                  </Card.Body>
                </Card>
              </Tab>

              {/* Organizations Tab */}
              <Tab eventKey="organizations" title="Organizations">
                <Card>
                  <Card.Header className="d-flex justify-content-between align-items-center">
                    <h6 className="mb-0">Organizations Overview</h6>
                    <Button variant="outline-secondary" size="sm" onClick={loadOrganizations}>
                      {loadingOrgs ? <Spinner size="sm" /> : 'Refresh'}
                    </Button>
                  </Card.Header>
                  <Card.Body>
                    {loadingOrgs ? (
                      <div className="text-center py-4">
                        <Spinner animation="border" />
                        <p className="mt-2">Loading organizations...</p>
                      </div>
                    ) : organizations.length === 0 ? (
                      <div className="text-center py-4 text-muted">
                        No organizations found.
                      </div>
                    ) : (
                      <div 
                        style={{ 
                          height: 400, 
                          width: '100%',
                          overflowX: 'auto',
                          overflowY: 'auto'
                        }}
                        className="table-responsive"
                      >
                        <div style={{ minWidth: isMobile ? 700 : '100%' }}>
                          <DataGrid
                            rows={organizations}
                            columns={[
                              { field: 'name', headerName: 'Organization Name', flex: 1.5, minWidth: 150 },
                              { field: 'email', headerName: 'Email', flex: 1.5, minWidth: 150 },
                              { field: 'plan', headerName: 'Plan', flex: 0.8, minWidth: 100,
                                renderCell: (params) => getStatusBadge(params.value) },
                              { field: 'memberCount', headerName: 'Members', flex: 0.8, minWidth: 100 },
                              { field: 'status', headerName: 'Status', flex: 0.8, minWidth: 100,
                                renderCell: (params) => getStatusBadge(params.value) },
                              { field: 'createdAt', headerName: 'Created', flex: 1, minWidth: 120,
                                renderCell: (params) => formatDate(params.value) }
                            ]}
                            pageSize={10}
                            rowsPerPageOptions={[10, 25, 50]}
                            disableSelectionOnClick
                            autoHeight={false}
                          />
                        </div>
                      </div>
                    )}
                  </Card.Body>
                </Card>
              </Tab>

              {/* Payouts Tab */}
              <Tab eventKey="payouts" title="Payouts">
                <Card>
                  <Card.Header className="d-flex justify-content-between align-items-center">
                    <h6 className="mb-0">Payouts Management</h6>
                    <Button variant="outline-secondary" size="sm" onClick={loadPayouts}>
                      {loadingPayouts ? <Spinner size="sm" /> : 'Refresh'}
                    </Button>
                  </Card.Header>
                  <Card.Body>
                    {loadingPayouts ? (
                      <div className="text-center py-4">
                        <Spinner animation="border" />
                        <p className="mt-2">Loading payouts...</p>
                      </div>
                    ) : payouts.length === 0 ? (
                      <div className="text-center py-4 text-muted">
                        No payouts found.
                      </div>
                    ) : (
                      <div 
                        style={{ 
                          height: 400, 
                          width: '100%',
                          overflowX: 'auto',
                          overflowY: 'auto'
                        }}
                        className="table-responsive"
                      >
                        <div style={{ minWidth: isMobile ? 800 : '100%' }}>
                          <DataGrid
                            rows={payouts}
                            columns={[
                              { field: 'orgName', headerName: 'Organization', flex: 1.5, minWidth: 150 },
                              { field: 'eventName', headerName: 'Event', flex: 1.2, minWidth: 150 },
                              { field: 'amount', headerName: 'Amount', flex: 1, minWidth: 120,
                              renderCell: (params) => formatCurrency(params.value, params.row.currency) },
                              { field: 'status', headerName: 'Status', flex: 0.8, minWidth: 100,
                              renderCell: (params) => getStatusBadge(params.value) },
                            { field: 'timestamp', headerName: 'Date', flex: 1, minWidth: 120,
                              renderCell: (params) => formatDate(params.value) },
                            { field: 'description', headerName: 'Description', flex: 1.5, minWidth: 150 }
                          ]}
                          pageSize={10}
                          rowsPerPageOptions={[10, 25, 50]}
                          disableSelectionOnClick
                          autoHeight={false}
                        />
                        </div>
                      </div>
                    )}
                  </Card.Body>
                </Card>
              </Tab>

              {/* Live Events Tab */}
              <Tab eventKey="liveevents" title="Live Events">
                <Card>
                  <Card.Header className="d-flex justify-content-between align-items-center">
                    <h6 className="mb-0">Events Management</h6>
                    <Button variant="outline-secondary" size="sm" onClick={loadLiveEvents}>
                      {loadingLiveEvents ? <Spinner size="sm" /> : 'Refresh'}
                    </Button>
                  </Card.Header>
                  <Card.Body>
                    <div className="mb-3">
                      <Button
                        variant={liveEventFilter === "live" ? "primary" : "outline-primary"}
                        size="sm"
                        onClick={() => {
                          setLiveEventFilter("live");
                          loadLiveEvents();
                        }}
                        className="me-2"
                      >
                        {liveEventFilter === "live" ? "✓ " : ""}Live Events
                      </Button>
                      <Button
                        variant={liveEventFilter === "draft" ? "primary" : "outline-primary"}
                        size="sm"
                        onClick={() => {
                          setLiveEventFilter("draft");
                          loadLiveEvents();
                        }}
                        className="me-2"
                      >
                        {liveEventFilter === "draft" ? "✓ " : ""}Draft Events
                      </Button>
                      <Button
                        variant={liveEventFilter === "deleted" ? "primary" : "outline-primary"}
                        size="sm"
                        onClick={() => {
                          setLiveEventFilter("deleted");
                          loadLiveEvents();
                        }}
                        className="me-2"
                      >
                        {liveEventFilter === "deleted" ? "✓ " : ""}Deleted Events
                      </Button>
                      <Button
                        variant={liveEventFilter === "all" ? "primary" : "outline-primary"}
                        size="sm"
                        onClick={() => {
                          setLiveEventFilter("all");
                          loadLiveEvents();
                        }}
                      >
                        {liveEventFilter === "all" ? "✓ " : ""}All Events
                      </Button>
                    </div>
                    {loadingLiveEvents ? (
                      <div className="text-center py-4">
                        <Spinner animation="border" />
                        <p className="mt-2">Loading {liveEventFilter === "live" ? "live" : liveEventFilter === "draft" ? "draft" : liveEventFilter === "deleted" ? "deleted" : "all"} events...</p>
                      </div>
                    ) : liveEvents.length === 0 ? (
                      <div className="text-center py-4 text-muted">
                        No {liveEventFilter === "live" ? "live" : liveEventFilter === "draft" ? "draft" : liveEventFilter === "deleted" ? "deleted" : ""} events found.
                      </div>
                    ) : (
                      <div 
                        style={{ 
                          height: 400, 
                          width: '100%',
                          overflowX: 'auto',
                          overflowY: 'auto'
                        }}
                        className="table-responsive"
                      >
                        <div style={{ minWidth: isMobile ? 800 : '100%' }}>
                          <DataGrid
                            rows={liveEvents}
                            columns={[
                              { field: 'name', headerName: liveEventFilter === "live" ? 'Live Event Name' : liveEventFilter === "draft" ? 'Draft Event Name' : liveEventFilter === "deleted" ? 'Deleted Event Name' : 'Event Name', flex: 1.5, minWidth: 150 },
                              { field: 'startDate', headerName: 'Start Date', flex: 1, minWidth: 120,
                                renderCell: (params) => formatDate(params.value) },
                              { field: 'endDate', headerName: 'End Date', flex: 1, minWidth: 120,
                                renderCell: (params) => formatDate(params.value) },
                              { field: 'location', headerName: 'Location', flex: 1, minWidth: 120 },
                              { field: 'totalOrdersAmount', headerName: 'Orders Total', flex: 1, minWidth: 120,
                                renderCell: (params) => formatCurrency(params.value) },
                              { field: 'payoutStatus', headerName: 'Payout Status', flex: 1.2, minWidth: 130,
                                renderCell: (params) => {
                                  const status = params.value;
                                  let badgeVariant = 'secondary';
                                  if (status === 'Fully Paid') badgeVariant = 'success';
                                  else if (status.includes('Partial Paid')) badgeVariant = 'warning';
                                  else if (status === 'Pending') badgeVariant = 'danger';
                                  else if (status === 'No Payouts') badgeVariant = 'secondary';
                                  
                                  return <Badge bg={badgeVariant}>{status}</Badge>;
                                }},
                              { field: 'status', headerName: 'Event Status', flex: 0.8, minWidth: 100,
                                renderCell: (params) => getStatusBadge(params.value) },
                              { field: 'orgId', headerName: 'Org ID', flex: 0.8, minWidth: 100 }
                            ]}
                            pageSize={10}
                            rowsPerPageOptions={[10, 25, 50]}
                            disableSelectionOnClick
                            autoHeight={false}
                          />
                        </div>
                      </div>
                    )}
                  </Card.Body>
                </Card>
              </Tab>

              {/* Admins Tab */}
              <Tab eventKey="admins" title="Admins">
                <Card>
                  <Card.Header className="d-flex justify-content-between align-items-center">
                    <h6 className="mb-0">All Admins</h6>
                    <Button variant="outline-secondary" size="sm" onClick={loadAdmins}>
                      {loadingAdmins ? <Spinner size="sm" /> : 'Refresh'}
                    </Button>
                  </Card.Header>
                  <Card.Body>
                    {loadingAdmins ? (
                      <div className="text-center py-4">
                        <Spinner animation="border" />
                        <p className="mt-2">Loading admins...</p>
                      </div>
                    ) : admins.length === 0 ? (
                      <div className="text-center py-4 text-muted">
                        No admins found.
                      </div>
                    ) : (
                      <div 
                        style={{ 
                          height: 400, 
                          width: '100%',
                          overflowX: 'auto',
                          overflowY: 'auto'
                        }}
                        className="table-responsive"
                      >
                        <div style={{ minWidth: isMobile ? 900 : '100%' }}>
                          <DataGrid
                            rows={admins}
                            columns={[
                              { field: 'name', headerName: 'Name', flex: 1.5, minWidth: 150 },
                              { field: 'email', headerName: 'Email', flex: 2, minWidth: 200 },
                              { field: 'birthday', headerName: 'Birthday', flex: 1, minWidth: 120 },
                              { 
                              field: 'roles', 
                              headerName: 'Roles', 
                              flex: 1.5,
                              minWidth: 150,
                              renderCell: (params) => (
                                <div>
                                  {params.value && params.value.length > 0 ? (
                                    params.value.map((role, index) => (
                                      <Badge key={index} bg="primary" className="me-1">
                                        {role}
                                      </Badge>
                                    ))
                                  ) : (
                                    <Badge bg="secondary">No roles</Badge>
                                  )}
                                </div>
                              )
                            },
                            { 
                              field: 'status', 
                              headerName: 'Status', 
                              flex: 0.8,
                              minWidth: 100,
                              renderCell: (params) => getStatusBadge(params.value) 
                            },
                            { 
                              field: 'createdAt', 
                              headerName: 'Created', 
                              flex: 1,
                              minWidth: 120,
                              renderCell: (params) => params.value ? formatDate(params.value) : 'N/A'
                            },
                            { 
                              field: 'lastLogin', 
                              headerName: 'Last Login', 
                              flex: 1,
                              minWidth: 120,
                              renderCell: (params) => params.value ? formatDate(params.value) : 'Never'
                            }
                          ]}
                          pageSize={10}
                          rowsPerPageOptions={[10, 25, 50]}
                          disableSelectionOnClick
                          autoHeight={false}
                        />
                        </div>
                      </div>
                    )}
                  </Card.Body>
                </Card>
              </Tab>

              {/* Page Views Tab */}
              <Tab eventKey="pageviews" title="Page Views">
                <Card>
                  <Card.Header className="d-flex justify-content-between align-items-center">
                    <h6 className="mb-0">Recent Page Views</h6>
                    <Button variant="outline-secondary" size="sm" onClick={loadPageViews}>
                      {loadingPageViews ? <Spinner size="sm" /> : 'Refresh'}
                    </Button>
                  </Card.Header>
                  <Card.Body>
                    {loadingPageViews ? (
                      <div className="text-center py-4">
                        <Spinner animation="border" />
                        <p className="mt-2">Loading page views...</p>
                      </div>
                    ) : pageViews.length === 0 ? (
                      <div className="text-center py-4 text-muted">
                        No page views found.
                      </div>
                    ) : (
                      <div 
                        style={{ 
                          height: 400, 
                          width: '100%',
                          overflowX: 'auto',
                          overflowY: 'auto'
                        }}
                        className="table-responsive"
                      >
                        <div style={{ minWidth: isMobile ? 800 : '100%' }}>
                          <DataGrid
                            rows={pageViews}
                            columns={[
                              { field: 'page', headerName: 'Page', flex: 2, minWidth: 200 },
                              { field: 'timestamp', headerName: 'Time', flex: 1.5, minWidth: 150,
                                renderCell: (params) => formatDate(params.value) },
                              { field: 'ip', headerName: 'IP Address', flex: 1.2, minWidth: 120 },
                              { field: 'userId', headerName: 'User ID', flex: 1, minWidth: 100 },
                              { field: 'sessionId', headerName: 'Session', flex: 1.2, minWidth: 120 }
                            ]}
                            pageSize={10}
                            rowsPerPageOptions={[10, 25, 50]}
                            disableSelectionOnClick
                            autoHeight={false}
                          />
                        </div>
                      </div>
                    )}
                  </Card.Body>
                </Card>
              </Tab>

              {/* Shortcuts Tab */}
              <Tab eventKey="shortcuts" title="Shortcuts">
                <div>
                  {/* Overview */}
                  {hasDataRole && (
                    <Card className="mb-3">
                      <Card.Header>
                        <h6 className="mb-0">Overview</h6>
                      </Card.Header>
                      <Card.Body>
                        <Row>
                          <Col md={4} className="mb-2">
                            <Button
                              variant="outline-primary"
                              className="w-100"
                              onClick={() => window.location.href = '/admin-organization-overview'}
                            >
                              <i className="fas fa-building me-2"></i>
                              Organization Overview
                            </Button>
                          </Col>
                          <Col md={4} className="mb-2">
                            <Button
                              variant="outline-primary"
                              className="w-100"
                              onClick={() => window.location.href = '/admin-event-overview'}
                            >
                              <i className="fas fa-calendar-alt me-2"></i>
                              Event Overview
                            </Button>
                          </Col>
                        </Row>
                      </Card.Body>
                    </Card>
                  )}

                  <Card className="mb-3">
                    <Card.Header>
                      <h6 className="mb-0">User Management</h6>
                    </Card.Header>
                    <Card.Body>
                      <Row>
                        <Col md={4} className="mb-2">
                          <Button
                            variant="outline-primary"
                            className="w-100"
                            onClick={() => window.location.href = '/admin-user-tags'}
                          >
                            <i className="fas fa-tags me-2"></i>
                            User Tags
                          </Button>
                        </Col>
                      </Row>
                    </Card.Body>
                  </Card>

                  {/* Business Management */}
                  <Card className="mb-3">
                    <Card.Header>
                      <h6 className="mb-0">Lifestyle Management</h6>
                    </Card.Header>
                    <Card.Body>
                      <Row>
                        <Col md={4} className="mb-2">
                          <Button
                            variant="outline-primary"
                            className="w-100"
                            onClick={() => window.location.href = '/admin-all-businesses'}
                          >
                            <i className="fas fa-store me-2"></i>
                            All Lifestyles
                          </Button>
                        </Col>
                        <Col md={4} className="mb-2">
                          <Button
                            variant="outline-primary"
                            className="w-100"
                            onClick={() => window.location.href = '/admin-tourism-event-creator'}
                          >
                            <i className="fas fa-globe me-2"></i>
                            Tourism Event Creator
                          </Button>
                        </Col>
                        <Col md={4} className="mb-2">
                          <Button
                            variant="outline-primary"
                            className="w-100"
                            onClick={() => window.location.href = '/admin-event-management'}
                          >
                            <i className="fas fa-star me-2"></i>
                            Event Management
                          </Button>
                        </Col>
                      </Row>
                    </Card.Body>
                  </Card>

                  {/* Analytics & Data */}
                  {hasManagementRole && (
                    <Card className="mb-3">
                      <Card.Header>
                        <h6 className="mb-0">Analytics & Data</h6>
                      </Card.Header>
                      <Card.Body>
                        <Row>
                          <Col md={4} className="mb-2">
                            <Button
                              variant="outline-success"
                              className="w-100"
                              onClick={() => window.location.href = '/analytics-admin'}
                            >
                              <i className="fas fa-chart-line me-2"></i>
                              Analytics
                            </Button>
                          </Col>
                          {/* <Col md={4} className="mb-2">
                            <Button
                              variant="outline-success"
                              className="w-100"
                              onClick={() => window.location.href = '/admin-monthly-analytics'}
                            >
                              <i className="fas fa-chart-bar me-2"></i>
                              Monthly Analytics
                            </Button>
                          </Col> */}
                          <Col md={4} className="mb-2">
                            <Button
                              variant="outline-success"
                              className="w-100"
                              onClick={() => window.location.href = '/admin-page-views'}
                            >
                              <i className="fas fa-eye me-2"></i>
                              Page Views
                            </Button>
                          </Col>
                          <Col md={4} className="mb-2">
                            <Button
                              variant="outline-success"
                              className="w-100"
                              onClick={() => window.location.href = '/admin-page-view-map'}
                            >
                              <i className="fas fa-map-marker-alt me-2"></i>
                              Page View Map
                            </Button>
                          </Col>
                          <Col md={4} className="mb-2">
                            <Button
                              variant="outline-success"
                              className="w-100"
                              onClick={() => window.location.href = '/admin-all-org'}
                            >
                              <i className="fas fa-building me-2"></i>
                              All Organizations
                            </Button>
                          </Col>
                          <Col md={4} className="mb-2">
                            <Button
                              variant="outline-success"
                              className="w-100"
                              onClick={() => navigate('/admin-all-payouts')}
                            >
                              <i className="fas fa-money-bill-wave me-2"></i>
                              All Payouts
                            </Button>
                          </Col>
                        </Row>
                      </Card.Body>
                    </Card>
                  )}

                  {/* Email Tools */}
                  <Card className="mb-3">
                    <Card.Header>
                      <h6 className="mb-0">Email Tools</h6>
                    </Card.Header>
                    <Card.Body>
                      <Row>
                        <Col md={4} className="mb-2">
                          <Button
                            variant="outline-info"
                            className="w-100"
                            onClick={() => window.location.href = '/admin-email-sender'}
                          >
                            <i className="fas fa-envelope me-2"></i>
                            Email Sender
                          </Button>
                        </Col>
                        <Col md={4} className="mb-2">
                          <Button
                            variant="outline-info"
                            className="w-100"
                            onClick={() => window.location.href = '/admin-email-campaign'}
                          >
                            <i className="fas fa-bullhorn me-2"></i>
                            Email Campaign
                          </Button>
                        </Col>
                        <Col md={4} className="mb-2">
                          <Button
                            variant="outline-info"
                            className="w-100"
                            onClick={() => window.location.href = '/admin-weekly-email-campaign'}
                          >
                            <i className="fas fa-calendar-week me-2"></i>
                            Weekly Email Campaign (EventGo)
                          </Button>
                        </Col>
                        <Col md={4} className="mb-2">
                          <Button
                            variant="outline-info"
                            className="w-100"
                            onClick={() => window.location.href = '/email-template-creator'}
                          >
                            <i className="fas fa-file-alt me-2"></i>
                            Email Template Creator
                          </Button>
                        </Col>
                      </Row>
                    </Card.Body>
                  </Card>

                  {/* User & Order Management
                  {hasManagementRole && (
                    <Card className="mb-3">
                      <Card.Header>
                        <h6 className="mb-0">User & Order Management</h6>
                      </Card.Header>
                      <Card.Body>
                        <Row>
                          <Col md={4} className="mb-2">
                            <Button
                              variant="outline-warning"
                              className="w-100"
                              onClick={() => window.location.href = '/user-management'}
                            >
                              <i className="fas fa-users me-2"></i>
                              User Management
                            </Button>
                          </Col>
                          <Col md={4} className="mb-2">
                            <Button
                              variant="outline-warning"
                              className="w-100"
                              onClick={() => window.location.href = '/order-management'}
                            >
                              <i className="fas fa-shopping-cart me-2"></i>
                              Order Management
                            </Button>
                          </Col>
                        </Row>
                      </Card.Body>
                    </Card>
                  )} */}

                  {/* Event Management */}
                  <Card className="mb-3">
                    <Card.Header>
                      <h6 className="mb-0">Event Management</h6>
                    </Card.Header>
                    <Card.Body>
                      <Row>
                        <Col md={4} className="mb-2">
                          <Button
                            variant="outline-secondary"
                            className="w-100"
                            onClick={() => window.location.href = '/bulk-upload-events'}
                          >
                            <i className="fas fa-upload me-2"></i>
                            Bulk Upload Events
                          </Button>
                        </Col>
                        <Col md={4} className="mb-2">
                          <Button
                            variant="outline-secondary"
                            className="w-100"
                            onClick={() => window.location.href = '/checkin-all'}
                          >
                            <i className="fas fa-check-circle me-2"></i>
                            Check-In System
                          </Button>
                        </Col>
                        <Col md={4} className="mb-2">
                          <Button
                            variant="outline-secondary"
                            className="w-100"
                            onClick={() => navigate('/admin-task-management')}
                          >
                            <i className="fas fa-tasks me-2"></i>
                            Task Management
                          </Button>
                        </Col>
                      </Row>
                    </Card.Body>
                  </Card>

                  {/* Data Tools */}
                  {hasDataRole && (
                    <Card className="mb-3">
                      <Card.Header>
                        <h6 className="mb-0">Data Tools</h6>
                      </Card.Header>
                      <Card.Body>
                        <Row>
                          <Col md={4} className="mb-2">
                            <Button
                              variant="outline-primary"
                              className="w-100"
                              onClick={() => window.location.href = '/admin-order-email-data'}
                            >
                              <i className="fas fa-database me-2"></i>
                              Order Email Data Gaps
                            </Button>
                          </Col>
                        </Row>
                      </Card.Body>
                    </Card>
                  )}

                  {/* Admin Tools */}
                  <Card className="mb-3">
                    <Card.Header>
                      <h6 className="mb-0">Admin Tools</h6>
                    </Card.Header>
                    <Card.Body>
                      <Row>
                        <Col md={4} className="mb-2">
                          <Button
                            variant="outline-dark"
                            className="w-100"
                            onClick={() => window.location.href = '/view-admin-and-roles'}
                          >
                            <i className="fas fa-user-shield me-2"></i>
                            View Admin & Roles
                          </Button>
                        </Col>
                        <Col md={4} className="mb-2">
                          <Button
                            variant="outline-dark"
                            className="w-100"
                            onClick={() => window.location.href = '/admin-dashboard'}
                          >
                            <i className="fas fa-tachometer-alt me-2"></i>
                            Legacy Admin Dashboard
                          </Button>
                        </Col>
                        <Col md={4} className="mb-2">
                          <Button
                            variant="outline-dark"
                            className="w-100"
                            onClick={() => window.location.href = '/legacy-dashboard'}
                          >
                            <i className="fas fa-history me-2"></i>
                            Legacy Dashboard
                          </Button>
                        </Col>
                      </Row>
                    </Card.Body>
                  </Card>
                </div>
              </Tab>
            </Tabs>
          </Card.Body>
        </Card>
      </div>
    </Container>
  );
}
